import React, { useRef } from "react";
import { Modal, Col } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";
import DOMPurify from "dompurify";
import Button from "../Button/Button";
import "./Isocertificatemodal.css";

const TERMS_AND_CONDITIONS = `
By submitting this form and accessing the ISO Certificate, you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not proceed with the form submission.`;

const ISOCertificateModal = ({ showModal, handleClose }) => {
    const recaptchaRef = useRef(null);

    const formik = useFormik({
        initialValues: {
            firstName: "",
            lastName: "",
            email: "",
            companyName: "",
            remarks: "",
            termsAccepted: false,
        },
        validationSchema: Yup.object({
            firstName: Yup.string().required("First Name is required"),
            lastName: Yup.string().required("Last Name is required"),
            email: Yup.string()
                .email("Invalid email address")
                .required("Work Email is required"),
            companyName: Yup.string().required("Company Name is required"),
            remarks: Yup.string().required("Remarks is required"),
            termsAccepted: Yup.boolean().oneOf(
                [true],
                "You must accept the Terms and Conditions"
            ),
        }),
        onSubmit: async (values, { resetForm }) => {
            try {
                const recaptchaToken = await recaptchaRef.current?.executeAsync();

                if (!recaptchaToken) {
                    console.error("Captcha verification failed. Please try again.");
                    return;
                }

                recaptchaRef.current?.reset();

                const apiUrl = `${process.env.REACT_APP_API_URL}/security`;

                const sanitizedValues = {
                    firstName: DOMPurify.sanitize(values.firstName),
                    lastName: DOMPurify.sanitize(values.lastName),
                    email: DOMPurify.sanitize(values.email),
                    companyName: DOMPurify.sanitize(values.companyName),
                    remarks: DOMPurify.sanitize(values.remarks),
                };

                const formData = {
                    ...sanitizedValues,
                    // service: window.location.pathname,
                    recaptchaToken: recaptchaToken,
                };
                console.log("Payload being sent:", formData);
                const response = await fetch(apiUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        // "x-api-key": process.env.REACT_APP_X_API_KEY,
                    },
                    body: JSON.stringify(formData),
                });

                if (response.ok) {
                    resetForm();
                    handleClose();
                    window.open("/security/ISO-Certificate.pdf", "_blank");
                } else {
                    console.error("Error submitting form");
                    window.open("/security/ISO-Certificate.pdf", "_blank");
                    resetForm();
                }
            } catch (error) {
                console.error("Error submitting form", error);
                window.open("/security/ISO-Certificate.pdf", "_blank");
                resetForm();
            }
        },
    });

    const handleModalClose = () => {
        formik.resetForm();
        handleClose();
    };

    return (
        <Modal
            show={showModal}
            onHide={handleModalClose}
            centered
            className="iso-certificate-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title>Download ISO Certificate</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="iso-modal-subtitle">
                    Please fill in your details to access the ISO Certificate.
                </p>
                <form className="d-flex flex-column" onSubmit={formik.handleSubmit}>

                    {/* Row 1: First Name + Last Name */}
                    <div className="iso-row">
                        <div className="iso-col">
                            <Col className="input-tag my-2 border">
                                <input
                                    className="w-100 bg-white py-2 px-3 border-0"
                                    type="text"
                                    placeholder="First Name*"
                                    {...formik.getFieldProps("firstName")}
                                />
                            </Col>
                            {formik.touched.firstName && formik.errors.firstName && (
                                <div className="text-danger iso-error">{formik.errors.firstName}</div>
                            )}
                        </div>
                        <div className="iso-col">
                            <Col className="input-tag my-2 border">
                                <input
                                    className="w-100 bg-white py-2 px-3 border-0"
                                    type="text"
                                    placeholder="Last Name*"
                                    {...formik.getFieldProps("lastName")}
                                />
                            </Col>
                            {formik.touched.lastName && formik.errors.lastName && (
                                <div className="text-danger iso-error">{formik.errors.lastName}</div>
                            )}
                        </div>
                    </div>

                    {/* Row 2: Work Email + Company Name */}
                    <div className="iso-row">
                        <div className="iso-col">
                            <Col className="input-tag my-2 border">
                                <input
                                    className="w-100 bg-white py-2 px-3 border-0"
                                    type="email"
                                    placeholder="Work Email*"
                                    {...formik.getFieldProps("email")}
                                />
                            </Col>
                            {formik.touched.email && formik.errors.email && (
                                <div className="text-danger iso-error">{formik.errors.email}</div>
                            )}
                        </div>
                        <div className="iso-col">
                            <Col className="input-tag my-2 border">
                                <input
                                    className="w-100 bg-white py-2 px-3 border-0"
                                    type="text"
                                    placeholder="Company Name*"
                                    {...formik.getFieldProps("companyName")}
                                />
                            </Col>
                            {formik.touched.companyName && formik.errors.companyName && (
                                <div className="text-danger iso-error">{formik.errors.companyName}</div>
                            )}
                        </div>
                    </div>

                    {/* Row 3: Remarks full width */}
                    <Col className="input-tag-message my-2 border">
                        <textarea
                            className="w-100 bg-white py-2 px-3 border-0"
                            placeholder="Remarks*"
                            rows={2}
                            {...formik.getFieldProps("remarks")}
                        />
                    </Col>
                    {formik.touched.remarks && formik.errors.remarks && (
                        <div className="text-danger iso-error">{formik.errors.remarks}</div>
                    )}

                    {/* Terms and Conditions */}
                    <div className="iso-terms-wrapper">
                        <label className="iso-terms-label">Terms and Conditions</label>
                        <div className="iso-terms-scroll">
                            <pre className="iso-terms-text">{TERMS_AND_CONDITIONS}</pre>
                        </div>
                    </div>

                    {/* Accept Checkbox */}
                    <div className="iso-checkbox-wrapper">
                        <input
                            type="checkbox"
                            id="termsAccepted"
                            className="iso-checkbox"
                            {...formik.getFieldProps("termsAccepted")}
                            checked={formik.values.termsAccepted}
                        />
                        <label htmlFor="termsAccepted" className="iso-checkbox-label">
                            I have read and agree to the Terms and Conditions
                        </label>
                    </div>
                    {formik.touched.termsAccepted && formik.errors.termsAccepted && (
                        <div className="text-danger iso-error">{formik.errors.termsAccepted}</div>
                    )}

                    <ReCAPTCHA
                        ref={recaptchaRef}
                        size="invisible"
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    />

                    <Button
                        type="submit"
                        className="button career-submit mt-4"
                        disabled={
                            formik.isSubmitting ||
                            !formik.values.firstName.trim() ||
                            !formik.values.lastName.trim() ||
                            !formik.values.email.trim() ||
                            !formik.values.companyName.trim() ||
                            !formik.values.remarks.trim() ||
                            !formik.values.termsAccepted
                        }
                    >
                        {/* {formik.isSubmitting ? "Submitting..." : "Submit & View Certificate"} */}
                        {formik.isSubmitting ? "Submitting..." : "Submit"}
                    </Button>
                </form>
            </Modal.Body>
        </Modal>
    );
};

export default ISOCertificateModal;